import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import styled from "styled-components";
import { CustomIcon } from "components/icon/customIcon.component";
import { useTranslation } from "react-i18next";

/** Props for the tag component. */
export interface TagProps {
  text: string;
  backgroundColor?: ColorV2;
  textColor?: ColorV2;
  onClear?: () => void;
  maxWidth?: number;
}

/** Tag element with optional clear action. */
export const Tag = ({
  text,
  backgroundColor = ColorV2.Blue6,
  textColor = ColorV2.Blue4,
  onClear,
  maxWidth,
}: TagProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <TagWrapper
      $backgroundColor={backgroundColor}
      $textColor={textColor}
      $maxWidth={maxWidth}
    >
      <Label $maxWidth={maxWidth}>{text}</Label>
      {onClear && (
        <CloseButton
          type="button"
          onClick={onClear}
          aria-label={t("DesignComponents.Remove", {
            text: `${text}`,
          })}
        >
          <CustomIcon name="v2/cross" size="16px" color={textColor} />
        </CloseButton>
      )}
    </TagWrapper>
  );
};

const TagWrapper = styled.span<{
  $backgroundColor: ColorV2;
  $textColor: ColorV2;
  $maxWidth?: number;
}>`
  ${TypographyV2.s1}
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  height: 32px;
  padding: 0 8px;
  gap: 8px;
  border-radius: 8px;
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  white-space: nowrap;
  min-width: 0;
  max-width: ${(props) => (props.$maxWidth ? `${props.$maxWidth}px` : "none")};
  overflow: hidden;
`;

const Label = styled.span<{ $maxWidth?: number }>`
  ${TypographyV2.s1}
  color: inherit;
  max-width: ${(props) => (props.$maxWidth ? "100%" : "none")};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
`;
